import { html, render } from 'lit-html';
import { connectionStatus } from '../services/connection-status.js';

/**
 * Offline Banner Component
 * Shows a notification banner when user is offline
 */

let bannerElement = null;
let isVisible = false;

const styles = `
  .offline-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    transform: translateY(-100%);
    transition: transform 0.3s ease-out, opacity 0.3s ease-out;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
  }

  .offline-banner.visible {
    transform: translateY(0);
    pointer-events: auto;
    opacity: 1;
    visibility: visible;
  }

  .offline-banner-content {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
    padding: 10px 16px;
    text-align: center;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .offline-banner-icon {
    font-size: 18px;
    animation: offline-pulse 2s infinite;
  }

  @keyframes offline-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .offline-banner-message {
    font-weight: 500;
  }

  .offline-banner-retry {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.2s;
    margin-left: 8px;
  }

  .offline-banner-retry:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

async function handleRetry() {
  const isOnline = await connectionStatus.checkConnectivity();
  if (isOnline) {
    window.location.reload();
  }
}

function renderBanner() {
  return html`
    <div class="offline-banner-content">
      <span class="offline-banner-icon">📡</span>
      <span class="offline-banner-message">Você está offline. Algumas funcionalidades podem não estar disponíveis.</span>
      <button class="offline-banner-retry" @click=${handleRetry}>Verificar conexão</button>
    </div>
  `;
}

function updateVisibility(online) {
  // Only show offline banner after verification to avoid false positives
  if (bannerElement) {
    if (!online) {
      // Double-check connectivity before showing - wait longer and verify
      setTimeout(async () => {
        // Check navigator.onLine AND try to fetch a resource
        const navigatorOffline = !navigator.onLine;
        let fetchFailed = false;

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          await fetch('/favicon.png', {
            method: 'HEAD',
            cache: 'no-store',
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch {
          fetchFailed = true;
        }

        // Only show if both checks indicate offline
        const isReallyOffline = navigatorOffline && fetchFailed;
        isVisible = isReallyOffline;

        if (isReallyOffline && bannerElement) {
          bannerElement.classList.add('visible');
        }
      }, 2000); // Wait 2 seconds before showing
    } else {
      isVisible = false;
      bannerElement.classList.remove('visible');
    }
  }
}

/**
 * Initialize the offline banner
 * Call this once during app initialization
 */
export function initOfflineBanner() {
  // Inject styles if not already present
  if (!document.getElementById('offline-banner-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'offline-banner-styles';
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
  }

  // Create banner element if not exists
  if (!bannerElement) {
    bannerElement = document.createElement('div');
    bannerElement.className = 'offline-banner';
    document.body.appendChild(bannerElement);
    render(renderBanner(), bannerElement);
  }

  // Subscribe to connection changes
  connectionStatus.subscribe(updateVisibility);
}

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  // Defer initialization to ensure DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOfflineBanner);
  } else {
    initOfflineBanner();
  }
}
